"use client";

import * as React from "react";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { api } from "~/trpc/react";
import {
  formatRecommendationsEmrLabel,
  formatRecommendationsSchedulingLabel,
  isEmrIntegrationId,
  recommendationsSchedulingProductLabel,
  writeHireRecommendationsSelection,
  type HireRecommendationsSelection,
  type MarketplaceEmrIntegrationId,
} from "~/lib/marketplace/hire-recommendations-pricing";
import type { MarketplaceSchedulingIntegrationId } from "~/lib/marketplace/hire-pricing";
import type { HireWizardToolCredentials } from "~/lib/marketplace/hire-wizard-draft";
import { writeHireCustomPreviewState } from "~/lib/marketplace/marketplace-hire-flow";
import { markHireBuilderOnboardingComplete } from "~/lib/marketplace/hire-builder-onboarding";
import { writeHireWizardContext } from "~/lib/marketplace/hire-wizard-steps";
import type { HireBuilderConnectAction } from "~/lib/marketplace/hire-builder";
import { startOAuthWithClerk } from "~/lib/oauth/start-oauth-with-clerk";
import { cn } from "~/lib/utils";

const GOOGLE_WORKSPACE_SERVER_IDS = new Set([
  "google-workspace-calendar",
  "pulsemcp-google-workspace-calendar",
]);

type CredentialField = {
  id: string;
  label: string;
  type?: "text" | "password";
  placeholder?: string;
  required?: boolean;
};

const SCHEDULING_CREDENTIAL_FIELDS: Partial<
  Record<MarketplaceSchedulingIntegrationId, CredentialField[]>
> = {
  calendly: [
    { id: "apiToken", label: "Personal Access Token", type: "password", required: true },
  ],
  zoom: [{ id: "apiKey", label: "API Key", type: "password", required: true }],
  google_calendar: [],
};

const EMR_CREDENTIAL_FIELDS: Partial<Record<MarketplaceEmrIntegrationId, CredentialField[]>> = {
  juvonno: [
    { id: "apiKey", label: "API Key", type: "password", required: true },
    {
      id: "baseUrl",
      label: "Base URL",
      placeholder: "https://tdemo.juvonno.com",
      required: true,
    },
  ],
  nexhealth: [{ id: "apiKey", label: "API Key", type: "password", required: true }],
  dentrix: [{ id: "apiKey", label: "API Key", type: "password", required: true }],
  dentrix_ascend: [{ id: "apiKey", label: "API Key", type: "password", required: true }],
  open_dental: [{ id: "apiKey", label: "API Key", type: "password", required: true }],
  eaglesoft: [{ id: "apiKey", label: "API Key", type: "password", required: true }],
  curve: [{ id: "apiKey", label: "API Key", type: "password", required: true }],
  denticon: [{ id: "apiKey", label: "API Key", type: "password", required: true }],
};

export type HireBuilderChatConnectPanelProps = {
  selection: HireRecommendationsSelection;
  onSelectionChange: (next: HireRecommendationsSelection) => void;
  credentials: HireWizardToolCredentials;
  onCredentialsChange: (next: HireWizardToolCredentials) => void;
  previewProjectId?: string | null;
  previewHireIntentId?: string | null;
  templateProjectId?: string | null;
  websiteUrl?: string;
  /** Explicit integration from Trish hire_action, or inferred from selection. */
  forcedIntegration?: HireBuilderConnectAction["integration"] | null;
  /** SiteGlue: hide unit add-on pricing language. */
  siteGluePlanMode?: boolean;
  /** Persist confirmed agent identity across Google OAuth full-page return. */
  agentIdentity?: {
    agentName?: string;
    jobTitle?: string;
    profileImageUrl?: string;
    agentNameConfirmed?: boolean;
  };
  className?: string;
};

function resolveActiveIntegration(
  selection: HireRecommendationsSelection,
  forced?: HireBuilderConnectAction["integration"] | null,
): HireBuilderConnectAction["integration"] | null {
  if (forced) return forced;
  if (selection.appointmentsEnabled && selection.schedulingIntegrationId !== "none") {
    const id = selection.schedulingIntegrationId;
    if (
      id === "google_calendar" ||
      id === "calendly" ||
      id === "zoom"
    ) {
      return id;
    }
  }
  if (selection.emrIntegrationEnabled && selection.emrIntegrationId !== "none") {
    const id = selection.emrIntegrationId;
    if (
      id === "juvonno" ||
      id === "nexhealth" ||
      id === "dentrix" ||
      id === "dentrix_ascend" ||
      id === "open_dental" ||
      id === "eaglesoft" ||
      id === "curve" ||
      id === "denticon"
    ) {
      return id;
    }
  }
  return null;
}

function integrationLabel(
  id: HireBuilderConnectAction["integration"],
  opts?: { includePrice?: boolean },
): string {
  const includePrice = opts?.includePrice !== false;
  if (isEmrIntegrationId(id)) {
    return includePrice
      ? formatRecommendationsEmrLabel(id)
      : recommendationsSchedulingProductLabel(id);
  }
  const schedulingId = id as MarketplaceSchedulingIntegrationId;
  return includePrice
    ? formatRecommendationsSchedulingLabel(schedulingId)
    : recommendationsSchedulingProductLabel(schedulingId);
}

export function HireBuilderChatConnectPanel({
  selection,
  onSelectionChange,
  credentials,
  onCredentialsChange,
  previewProjectId,
  previewHireIntentId,
  templateProjectId,
  websiteUrl,
  forcedIntegration,
  siteGluePlanMode = false,
  agentIdentity,
  className,
}: HireBuilderChatConnectPanelProps) {
  const utils = api.useUtils();
  const [showPassword, setShowPassword] = React.useState<Record<string, boolean>>({});
  const integration = resolveActiveIntegration(selection, forcedIntegration);

  const mcpConfigsQuery = api.project.getMCPConfigurations.useQuery(
    { projectId: previewProjectId! },
    { enabled: Boolean(previewProjectId) && integration === "google_calendar" },
  );

  const deleteConfigMutation = api.project.deleteMCPConfiguration.useMutation({
    onSuccess: () => {
      toast.success("Google Calendar disconnected.");
      if (previewProjectId) {
        void utils.project.getMCPConfigurations.invalidate({ projectId: previewProjectId });
      }
    },
    onError: (error) => {
      toast.error(error.message || "Could not disconnect Google Calendar.");
    },
  });

  if (!integration) return null;

  const isEmr = isEmrIntegrationId(integration);
  const credentialKey = isEmr ? `emr:${integration}` : `scheduling:${integration}`;
  const fields = isEmr
    ? (EMR_CREDENTIAL_FIELDS[integration as MarketplaceEmrIntegrationId] ?? [])
    : (SCHEDULING_CREDENTIAL_FIELDS[integration as MarketplaceSchedulingIntegrationId] ?? []);
  const values = credentials[credentialKey] ?? {};

  const googleConfig = mcpConfigsQuery.data?.find((config) =>
    GOOGLE_WORKSPACE_SERVER_IDS.has(config.serverId),
  );
  const googleCredentials = (googleConfig?.credentials as Record<string, string> | undefined) ?? {};
  const googleConnected = Boolean(
    googleConfig?.enabled && googleCredentials.googleRefreshToken?.trim(),
  );

  const ensureSelectionForIntegration = () => {
    if (isEmr) {
      onSelectionChange({
        ...selection,
        appointmentsEnabled: false,
        schedulingIntegrationId: "none",
        emrIntegrationEnabled: true,
        emrIntegrationId: integration as MarketplaceEmrIntegrationId,
      });
    } else {
      onSelectionChange({
        ...selection,
        appointmentsEnabled: true,
        schedulingIntegrationId: integration as MarketplaceSchedulingIntegrationId,
        emrIntegrationEnabled: false,
        emrIntegrationId: "none",
      });
    }
  };

  const connectGoogleCalendar = () => {
    if (!previewProjectId) {
      toast.error("Generate your agent Preview first, then connect Google Calendar.");
      return;
    }
    ensureSelectionForIntegration();
    writeHireRecommendationsSelection({
      ...selection,
      appointmentsEnabled: true,
      schedulingIntegrationId: "google_calendar",
    });
    markHireBuilderOnboardingComplete();
    if (previewHireIntentId) {
      writeHireCustomPreviewState({
        previewProjectId,
        previewHireIntentId,
        ...(templateProjectId ? { templateProjectId } : {}),
        ...(agentIdentity?.agentName?.trim()
          ? { agentName: agentIdentity.agentName.trim() }
          : {}),
        ...(agentIdentity?.jobTitle?.trim()
          ? { jobTitle: agentIdentity.jobTitle.trim() }
          : {}),
        ...(agentIdentity?.profileImageUrl?.trim()
          ? { profileImageUrl: agentIdentity.profileImageUrl.trim() }
          : {}),
        ...(agentIdentity?.agentNameConfirmed
          ? { agentNameConfirmed: true }
          : {}),
      });
    }
    if (websiteUrl?.trim()) {
      writeHireWizardContext(websiteUrl, "summary");
    }
    const params = new URLSearchParams({
      projectId: previewProjectId,
      channels: "chat",
      returnTo: `${window.location.pathname}${window.location.search}`,
    });
    void startOAuthWithClerk(`/api/mcp/google-oauth/start?${params.toString()}`).catch(
      (error) => {
        toast.error(error instanceof Error ? error.message : "Google OAuth start failed");
      },
    );
  };

  const updateField = (fieldId: string, value: string) => {
    onCredentialsChange({
      ...credentials,
      [credentialKey]: { ...values, [fieldId]: value },
    });
  };

  const label = integrationLabel(integration, {
    includePrice: !siteGluePlanMode,
  });
  const productLabel = integrationLabel(integration, { includePrice: false });

  return (
    <div
      className={cn(
        "rounded-lg border border-primary/20 bg-primary/5 p-3 shadow-sm",
        className,
      )}
    >
      {integration === "google_calendar" && googleConnected ? (
        <p className="text-sm font-semibold text-slate-900">Calendar connected</p>
      ) : (
        <>
          <p className="text-sm font-semibold text-slate-900">Connect {label}</p>
          <p className="mt-1 text-xs text-slate-600">
            {siteGluePlanMode
              ? "Included in your SiteGlue plan — finish this connect step in chat."
              : "Finish this in chat — no need to open Configure."}
          </p>
        </>
      )}

      {integration === "google_calendar" ? (
        <div className="mt-3 space-y-2">
          {googleConnected && googleCredentials.accountEmail ? (
            <p className="text-xs text-emerald-700">
              Connected as {googleCredentials.accountEmail}
            </p>
          ) : (
            <p className="text-xs text-slate-500">
              Connect your Google account so {productLabel} can check availability and book
              appointments.
            </p>
          )}
          <Button
            type="button"
            size="sm"
            variant={googleConnected ? "outline" : "default"}
            disabled={!previewProjectId || deleteConfigMutation.isPending || mcpConfigsQuery.isLoading}
            onClick={
              googleConnected
                ? () => {
                    if (!googleConfig?.id) return;
                    deleteConfigMutation.mutate({ configId: googleConfig.id });
                  }
                : connectGoogleCalendar
            }
          >
            {deleteConfigMutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : null}
            {googleConnected ? "Disconnect from Google" : "Connect to Google Account"}
          </Button>
          {!previewProjectId ? (
            <p className="text-[11px] text-slate-500">
              Available after your agent Preview is ready.
            </p>
          ) : null}
        </div>
      ) : (
        <div className="mt-3 space-y-3">
          {fields.map((field) => (
            <div key={field.id} className="space-y-1.5">
              <Label htmlFor={`chat-${credentialKey}-${field.id}`} className="text-xs">
                {field.label}
                {field.required ? " *" : ""}
              </Label>
              <div className="relative">
                <Input
                  id={`chat-${credentialKey}-${field.id}`}
                  type={
                    field.type === "password" && !showPassword[field.id] ? "password" : "text"
                  }
                  placeholder={field.placeholder}
                  value={values[field.id] ?? ""}
                  onChange={(e) => {
                    ensureSelectionForIntegration();
                    updateField(field.id, e.target.value);
                  }}
                  className={field.type === "password" ? "pr-10" : undefined}
                />
                {field.type === "password" ? (
                  <button
                    type="button"
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    onClick={() =>
                      setShowPassword((prev) => ({ ...prev, [field.id]: !prev[field.id] }))
                    }
                    aria-label={showPassword[field.id] ? "Hide" : "Show"}
                  >
                    {showPassword[field.id] ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                ) : null}
              </div>
            </div>
          ))}
          <p className="text-[11px] text-slate-500">
            Credentials are saved with your hire session and applied when you onboard.
          </p>
        </div>
      )}
    </div>
  );
}
